import * as React from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * Formatted numeric input. Shows thousands separators (and an optional currency
 * prefix) when idle, and the raw editable number while focused — so large
 * counts like `800,000` stay readable but cursor behaviour stays sane while
 * typing. Emits a parsed `number | null` (null = empty), never a string.
 *
 * Props beyond the native input:
 * - ``value`` / ``onChange`` — controlled number (null when blank).
 * - ``prefix`` — left adornment, e.g. ``"$"``.
 * - ``maxDecimals`` — fractional digits shown when idle (default 2). Trailing
 *   zeros are trimmed, so 100 shows as "100", 0.028 as "0.028".
 * - ``align`` — text alignment (right for money columns).
 * - ``label`` / ``helper`` — optional stacked label and hint, so a numeric
 *   field with a caveat ("freight on the order, not outbound shipping")
 *   doesn't have to be hand-wrapped in a div at every call site.
 */
export interface NumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "prefix"> {
  value: number | null;
  onChange: (value: number | null) => void;
  prefix?: string;
  maxDecimals?: number;
  align?: "left" | "right";
  /** Stacked label above the input. Requires `id` to associate the two. */
  label?: React.ReactNode;
  /** Muted hint below the input — the caveat the label has no room for. */
  helper?: React.ReactNode;
}

function groupInteger(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatNumber(value: number | null | undefined, maxDecimals = 2): string {
  if (value == null || Number.isNaN(value)) return "";
  const neg = value < 0;
  const parts = Math.abs(value).toFixed(maxDecimals).split(".");
  const int = parts[0] ?? "0";
  const dec = (parts[1] ?? "").replace(/0+$/, "");
  return `${neg ? "-" : ""}${groupInteger(int)}${dec ? `.${dec}` : ""}`;
}

function parseNumber(text: string): number | null {
  const cleaned = text.replace(/,/g, "").trim();
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isNaN(n) ? null : n;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      prefix,
      maxDecimals = 2,
      align = "left",
      label,
      helper,
      className,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = React.useState(false);
    const [draft, setDraft] = React.useState("");

    const display = focused ? draft : formatNumber(value, maxDecimals);
    const helperId = helper && props.id ? `${props.id}-helper` : undefined;

    const control = (
      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {prefix}
          </span>
        )}
        <Input
          ref={ref}
          inputMode="decimal"
          value={display}
          className={cn(prefix && "pl-6", align === "right" && "text-right", className)}
          onFocus={(e) => {
            setFocused(true);
            setDraft(value == null ? "" : String(value));
            onFocus?.(e);
          }}
          onChange={(e) => {
            const t = e.target.value;
            // Keep only digits, one dot, leading minus, and commas (stripped on parse).
            if (!/^-?[\d,]*\.?\d*$/.test(t)) return;
            setDraft(t);
            onChange(parseNumber(t));
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          aria-describedby={helperId}
          {...props}
        />
      </div>
    );

    if (!label && !helper) return control;
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <Label htmlFor={props.id} className="text-sm">
            {label}
          </Label>
        )}
        {control}
        {helper && (
          <p id={helperId} className="px-1 text-[11px] leading-tight text-muted-foreground">
            {helper}
          </p>
        )}
      </div>
    );
  },
);
NumberInput.displayName = "NumberInput";
